import React from "react";
import { Link } from "react-router-dom";
import {
  Camera,
  ClipboardCheck,
  Clock,
  MapPin,
  Route,
  ShieldCheck,
  Smartphone,
  TimerReset,
  Workflow,
} from "lucide-react";
import SeoManager from "../../components/SEO/SeoManager";

const BENEFICIOS = [
  {
    icon: <ClipboardCheck size={24} className="text-primaryBlue" />,
    title: "Checklists guiados",
    description: "Estandariza rutinas, inspecciones y validaciones con pasos obligatorios.",
  },
  {
    icon: <Clock size={24} className="text-primaryBlue" />,
    title: "Control de tiempos reales",
    description: "Mide inicio, fin, duracion y atrasos de cada actividad operativa.",
  },
  {
    icon: <Camera size={24} className="text-primaryBlue" />,
    title: "Evidencias verificables",
    description: "Captura fotos, notas y soportes para cerrar tareas con respaldo real.",
  },
  {
    icon: <MapPin size={24} className="text-primaryBlue" />,
    title: "Ubicacion y contexto",
    description: "Valida donde se ejecuto la actividad y quien estuvo a cargo.",
  },
];

const CASOS = [
  {
    icon: <Route size={22} className="text-primaryBlue" />,
    title: "Rondas y supervisiones",
    description: "Controla recorridos, puntos visitados y novedades detectadas en sitio.",
  },
  {
    icon: <TimerReset size={22} className="text-primaryBlue" />,
    title: "Servicios tecnicos",
    description: "Registra tiempos, evidencias y cierre de trabajo en mantenimiento o soporte.",
  },
  {
    icon: <ShieldCheck size={22} className="text-primaryBlue" />,
    title: "Auditorias operativas",
    description: "Asegura cumplimiento con evidencia antes de aprobar una inspeccion o entrega.",
  },
  {
    icon: <Workflow size={22} className="text-primaryBlue" />,
    title: "Procesos distribuidos",
    description: "Conecta cuadrillas, supervisores y backoffice en un flujo unico y trazable.",
  },
];

export default function ControlOperativo() {
  const canonicalUrl = "https://prodsyncapp.com/control-operativo-en-campo";
  const title = "Software de Control Operativo en Campo | PRODSYNC";
  const description =
    "Control operativo en campo con checklists digitales, tiempos reales, evidencias fotograficas y trazabilidad completa desde app movil.";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "PRODSYNC Control Operativo en Campo",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, Android",
    url: canonicalUrl,
    description,
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 antialiased">
      <SeoManager
        title={title}
        description={description}
        canonical={canonicalUrl}
        jsonLd={jsonLd}
      />

      <h1 className="sr-only">Control operativo en campo con trazabilidad, tiempos y evidencias</h1>

      <section className="py-20 bg-primaryDark text-white relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primaryBlue/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-6">
              <span className="inline-block px-4 py-1 rounded-full text-xs font-bold bg-primaryBlue/20 text-primaryBlue border border-primaryBlue/30 uppercase tracking-widest">
                Software de control operativo en campo
              </span>

              <p className="text-4xl md:text-6xl font-extrabold leading-tight">
                Ejecuta operaciones de campo con
                <br />
                <span className="text-primaryBlue">control y trazabilidad.</span>
              </p>

              <p className="text-slate-400 text-lg leading-relaxed max-w-lg">
                PRODSYNC digitaliza recorridos, inspecciones, mantenimientos,
                checklists y tareas en sitio para que supervises en tiempo real lo
                que pasa fuera de oficina.
              </p>

              <div className="grid sm:grid-cols-2 gap-3 pt-4">
                {BENEFICIOS.map((item) => (
                  <div
                    key={item.title}
                    className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:border-primaryBlue/50 transition-all"
                  >
                    <div>{item.icon}</div>
                    <div>
                      <p className="text-sm font-bold text-white">{item.title}</p>
                      <p className="text-xs text-slate-300">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-[2rem] p-8 lg:p-10 shadow-2xl">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Que puedes controlar en campo
              </h2>
              <div className="space-y-4">
                {CASOS.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-2xl border border-white/10 bg-slate-900/40 p-5"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      {item.icon}
                      <h3 className="text-lg font-bold text-white">{item.title}</h3>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-6 justify-center lg:justify-start">
            <Link
              to="/"
              state={{ scrollToForm: true }}
              className="inline-flex items-center gap-2 text-primaryBlue font-bold hover:text-primaryBlueHover transition-colors"
            >
              Solicitar demo ahora <span aria-hidden="true">-&gt;</span>
            </Link>
            <Link
              to="/software-para-manufactura"
              className="inline-flex items-center gap-2 text-white font-bold hover:text-primaryBlue transition-colors"
            >
              Ver solucion para manufactura <span aria-hidden="true">-&gt;</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white text-slate-900 rounded-t-[3rem] shadow-inner relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-5xl font-black italic tracking-tight">
              Opera en campo con pasos claros y evidencia obligatoria
            </h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto font-medium text-balance">
              Desde una inspeccion hasta un servicio tecnico, cada actividad queda
              registrada con hora, responsable, evidencia y estado.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {BENEFICIOS.map((item) => (
              <div
                key={item.title}
                className="bg-white p-7 rounded-[2.5rem] border border-slate-200 relative group transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
              >
                <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-6 shadow-inner group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 tracking-tight group-hover:text-primaryBlue transition-colors leading-snug">
                  {item.title}
                </h3>
                <p className="text-slate-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="p-8 lg:p-12 bg-slate-900 rounded-[3rem] flex flex-col lg:flex-row items-center justify-between gap-10 text-white shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primaryBlue/10 rounded-full blur-[100px]" />
            <div className="space-y-4 z-10 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-white/10 text-primaryBlue text-[10px] font-black uppercase tracking-widest">
                <Smartphone size={14} /> Ejecucion mobile
              </div>
              <h3 className="text-3xl md:text-4xl font-black italic tracking-tighter leading-none text-white">
                Del supervisor <span className="text-primaryBlue">al equipo en campo.</span>
              </h3>
              <p className="text-slate-400 text-lg max-w-xl font-medium leading-snug">
                La configuracion se traduce en una interfaz simple para el operario o
                tecnico, reduciendo errores y mejorando cumplimiento en cada visita o recorrido.
              </p>
            </div>

            <Link
              to="/"
              state={{ scrollToForm: true }}
              className="px-12 py-5 bg-primaryBlue hover:bg-primaryBlueHover text-white rounded-[1.5rem] font-black text-lg transition-all shadow-xl shadow-primaryBlue/20 whitespace-nowrap z-10 hover:scale-105 active:scale-95 text-center flex items-center justify-center"
            >
              Solicitar una demo
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-16 bg-white border-t border-slate-200 text-center px-6">
        <p className="text-slate-400 font-bold italic text-sm leading-relaxed max-w-2xl mx-auto">
          © 2026 - PRODSYNC. Control operativo en campo con trazabilidad y evidencias.
        </p>
      </footer>
    </div>
  );
}
